import {FC} from 'react';
import {Form, Formik, FormikConfig, FormikProps} from 'formik';
import {Box, Grid} from '@mui/material';
import {useIntl} from 'react-intl';
import {AMPInput} from '../../common/AMPInput';
import {getHelperText} from '../../../utils/forms';
import {CancelButton} from '../../common/CancelButton';
import {SubmitButton} from '../../common/SubmitButton';
import {createRfidFormSchema, CreateRfidFormType, NO_EMPLOYEE_OPTION} from '../formData/rfid/rfid';
import {CompanyEmployeeSelect} from '../CompanyEmployeeSelect';
import {tagsApi} from '../../../api';
import {AppDispatch} from '../../../app/store';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {useAppDispatch} from '../../../app/hooks';
import {CreateRfidFormProps} from './CreateRfidForm.types';
import {actionsWrapperSx, formContentSx, formSx} from './CreateRfidForm.sx';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

const initialValue: CreateRfidFormType = {
    rfidTag: '',
    licensePlate: '',
    employeeId: NO_EMPLOYEE_OPTION,
};

const handleSubmit = (
    createTagTrigger: ReturnType<typeof tagsApi.useCreateNewTagMutation>[0],
    linkTagTrigger: ReturnType<typeof tagsApi.useLinkTagToUserMutation>[0],
    dispatch: AppDispatch,
    userId: number | undefined,
    businessId: number | undefined,
    employeeId: number | undefined,
    companyId: number | undefined,
    handleClose: () => void,
    onSuccess?: () => void
): FormikConfig<CreateRfidFormType>['onSubmit'] => {
    return async (value) => {
        try {
            dispatch(preloaderChangeStatus(true));
            const {rfidTag, licensePlate} = value;

            if (businessId) {
                // Create a tag with employee-specific data if the employee exists
                const createTagData: any = {
                    businessId,
                    idTag: rfidTag,
                    licensePlate: licensePlate?.trim() ? licensePlate.trim() : null,
                };

                if (employeeId && companyId) {
                    createTagData.employeeId = employeeId;
                    createTagData.companyId = companyId;
                }

                const createTagResponse = await createTagTrigger({
                    data: createTagData,
                }).unwrap();

                // Link the created tag to the user only if it's not an employee case
                if (userId && !employeeId) {
                    await linkTagTrigger({
                        tagId: createTagResponse.id,
                        userId: userId,
                        params: {
                            businessId,
                        },
                    }).unwrap();
                }
            }
            onSuccess?.();
            handleClose();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };
};

const handleCompanySubmit = (
    createTagTrigger: ReturnType<typeof tagsApi.useCreateNewTagMutation>[0],
    linkTagTrigger: ReturnType<typeof tagsApi.useLinkTagToUserMutation>[0],
    dispatch: AppDispatch,
    companyUserId: number | undefined,
    businessId: number | undefined,
    companyId: number | undefined,
    handleClose: () => void,
    onSuccess?: () => void
): FormikConfig<CreateRfidFormType>['onSubmit'] => {
    return async (value) => {
        try {
            dispatch(preloaderChangeStatus(true));
            const {rfidTag, licensePlate, employeeId} = value;
            const selectedEmployeeId =
                employeeId && employeeId !== NO_EMPLOYEE_OPTION ? +employeeId : undefined;

            if (companyUserId && businessId) {
                if (selectedEmployeeId && companyId) {
                    // Assign the tag directly to the chosen company employee
                    await createTagTrigger({
                        data: {
                            businessId,
                            idTag: rfidTag,
                            licensePlate: licensePlate?.trim() ? licensePlate.trim() : null,
                            companyId,
                            employeeId: selectedEmployeeId,
                        },
                    }).unwrap();
                } else {
                    // Company-wide tag: create then link to the company user
                    const createTagResponse = await createTagTrigger({
                        data: {
                            businessId,
                            idTag: rfidTag,
                            licensePlate: licensePlate?.trim() ? licensePlate.trim() : null,
                        },
                    }).unwrap();

                    await linkTagTrigger({
                        tagId: createTagResponse.id,
                        userId: companyUserId,
                        params: {
                            businessId,
                        },
                    }).unwrap();
                }
            }
            onSuccess?.();
            handleClose();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };
};

export const CreateRfidForm: FC<CreateRfidFormProps> = ({
                                                            handleClose,
                                                            userData,
                                                            isCompanyUser = false,
                                                            employeeId,
                                                            companyId,
                                                            onSuccess,
                                                        }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [createTagTrigger] = tagsApi.useCreateNewTagMutation();
    const [linkTagTrigger] = tagsApi.useLinkTagToUserMutation();

    const defineSubmitAction = isCompanyUser
        ? handleCompanySubmit(
            createTagTrigger,
            linkTagTrigger,
            dispatch,
            userData?.id,
            userData?.business?.id,
            companyId ?? userData?.company?.id,
            handleClose,
            onSuccess
        )
        : handleSubmit(
            createTagTrigger,
            linkTagTrigger,
            dispatch,
            userData?.id,
            userData?.business?.id,
            employeeId,
            companyId,
            handleClose,
            onSuccess
        );

    return (
        <Formik
            initialValues={initialValue}
            validationSchema={createRfidFormSchema}
            onSubmit={defineSubmitAction}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<CreateRfidFormType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Box sx={formContentSx}>
                            <Grid container>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['rfidTag']}
                                        type={'rfidTag'}
                                        label={'rfidTag'}
                                        input={'rfidTag'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['rfidTag'] && Boolean(errors['rfidTag'])}
                                        helperText={getHelperText('rfidTag', touched, errors, intl)}
                                    />
                                </Grid>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['licensePlate']}
                                        type={'text'}
                                        label={'licensePlate'}
                                        input={'licensePlate'}
                                        placeholder={'licensePlatePlaceholder'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['licensePlate'] && Boolean(errors['licensePlate'])}
                                        helperText={getHelperText('licensePlate', touched, errors, intl)}
                                    />
                                </Grid>
                                {isCompanyUser && (
                                    <Grid
                                        xs={12}
                                        item
                                    >
                                        <CompanyEmployeeSelect
                                            businessId={userData?.business?.id}
                                            companyId={companyId ?? userData?.company?.id}
                                            value={values['employeeId']}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['employeeId'] && Boolean(errors['employeeId'])}
                                            helperText={getHelperText('employeeId', touched, errors, intl)}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                        <Box sx={actionsWrapperSx}>
                            <CancelButton
                                text="cancel"
                                handler={handleClose}
                            />
                            <SubmitButton
                                text="save"
                                color="primary"
                                isSubmitting={isSubmitting}
                            />
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
